import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import PropTypes from 'prop-types';
import { toast } from '../../toasts';
import { Button, FormField, controlClass } from '../../components/EntityList';
import generatedDebtService from '../../services/generatedDebts.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

/**
 * Monthly user-count confirmation for a 'perUser' debt due.
 * The due keeps a provisional amount until the count is confirmed here.
 */
export default function ConfirmCountModal({ show, onHide, due, onSaved }) {
	const { t } = useLabels();
	const [userCount, setUserCount] = useState('');
	const [saving, setSaving] = useState(false);
	const [fieldError, setFieldError] = useState('');

	useEffect(() => {
		if (due) {
			setUserCount(due.userCount !== undefined && due.userCount !== null ? String(due.userCount) : '');
			setFieldError('');
		}
	}, [due]);

	if (!due) return null;

	const pricePerUser = due.pricePerUserSnapshot ?? due.debt?.pricePerUser ?? 0;
	const parsedCount = Number(userCount);
	const validCount = Number.isInteger(parsedCount) && parsedCount >= 0;
	const total = validCount ? parsedCount * pricePerUser : null;

	async function handleConfirm() {
		if (!validCount) {
			setFieldError(t('msg.invalidUserCount'));
			toast.error(t('msg.invalidUserCount'));
			return;
		}
		setFieldError('');
		setSaving(true);
		try {
			await generatedDebtService.confirmCount(due._id, parsedCount);
			toast.success(t('msg.userCountConfirmed'));
			onHide();
			if (onSaved) onSaved();
		} catch (err) {
			toast.error(getError(err));
		}
		setSaving(false);
	}

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton>
				<Modal.Title>{t('title.confirmUserCount')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className='tw:mb-1 tw:text-[13px] tw:text-ink-1'>
					<b>{t('entity.due')}:</b> {due.customId}
				</p>
				<p className='tw:mb-3 tw:text-[13px] tw:text-ink-1'>
					<b>{t('form.pricePerUser')}:</b> {pricePerUser}
				</p>
				<Form onSubmit={(event) => { event.preventDefault(); handleConfirm(); }}>
					<FormField id='confirm-count-user-count' label={t('form.userCountMonth')} required>
						<Form.Control
							id='confirm-count-user-count'
							type='number'
							min='0'
							step='1'
							className={controlClass}
							value={userCount}
							autoFocus
							onChange={(e) => {
								setUserCount(e.target.value);
								setFieldError('');
							}}
						/>
						<FieldError message={fieldError} />
					</FormField>
				</Form>
				<p className='tw:mt-3 tw:mb-0 tw:text-[13px] tw:text-ink-1'>
					<b>{t('field.monthTotal')}:</b> {total !== null ? total : '-'}
				</p>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onHide} disabled={saving}>
					{t('action.cancel')}
				</Button>
				<Button variant='primary' onClick={handleConfirm} disabled={saving || !validCount}>
					{t('action.confirm')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
